
// Small helpers shared by the observer tracker modules.

export const uuid = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	// RFC 4122 v4 from getRandomValues (older Safari lacks randomUUID)
	const b = crypto.getRandomValues(new Uint8Array(16));
	b[6] = (b[6] & 0x0f) | 0x40;
	b[8] = (b[8] & 0x3f) | 0x80;
	const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
	return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

// FNV-1a; stable across page loads, used for sampling and text grouping
export const hash32 = (s: string): number => {
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return h >>> 0;
};

// Deterministic [0, 1) bucket for a string
export const unitHash = (s: string): number => hash32(s) / 0x100000000;

// Routes whose last segment is a per-person report key. Mirrors
// observer's app/core/normalize.py, which re-applies this server-side.
const KEYED_PREFIXES = [
	'/report-dexa/',
	'/shared-dexa/',
	'/iframe-dexa/',
	'/appview-dexa/',
];

// UUIDs, digit runs and long hex only. No generic "long token" rule: readable
// slugs like /blog/post/fat_loss_factors_post_1_sleep must stay distinct.
// Routes that carry opaque keys are listed in KEYED_PREFIXES instead.
const ID_SEGMENT = new RegExp([
	'^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$',
	'^\\d+$',
	'^[0-9a-f]{16,}$',
].join('|'), 'i');

export const normalizePath = (path: string): string => {
	for (const prefix of KEYED_PREFIXES) {
		if (path.startsWith(prefix) && path.length > prefix.length) {
			return prefix + ':key';
		}
	}
	const out = path
		.split('/')
		.map((seg) => ID_SEGMENT.test(seg) ? ':id' : seg)
		.join('/');
	return out.length > 1 && out.endsWith('/') ? out.slice(0, -1) : out;
};

// Privacy-safe link target for click events: no query or hash, internal
// paths normalized like event paths (so /report-dexa/<key> never leaks)
export const normalizeTarget = (href: string): string => {
	const bare = href.split(/[?#]/)[0];
	if (bare.startsWith('/')) {
		return normalizePath(bare);
	}
	try {
		const url = new URL(bare);
		if (typeof window !== 'undefined' && url.origin === window.location.origin) {
			return normalizePath(url.pathname);
		}
		return `${url.origin}${normalizePath(url.pathname)}`;
	} catch {
		// relative or malformed: treat as a path
		return normalizePath('/' + bare.replace(/^\.?\/*/, ''));
	}
};

// '/account-*' style globs: '*' matches anything, everything else literal
export const globToRegExp = (glob: string): RegExp => new RegExp(
	'^' + glob.split('*').map((p) => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$',
);

export const truncate = (s: string | undefined, n: number): string | undefined =>
	s === undefined ? undefined : s.length > n ? s.slice(0, n) : s;
